import { B2Card, B2Col, B2Row } from "@hdb/biome-2";
import { FC } from "react";
import { Spinner } from "react-bootstrap";
import formatEnergySavings from "src/shared/helpers/format-energy-savings";
import getLastMonthName, {
  getFullMonthName,
  getTwoMonthsAgoName,
} from "src/shared/helpers/last-month";
import roundNumber from "src/shared/helpers/math";
import DashboardTrandingChart from "./custom-trending-chart/DashboardTrandingChart";
import db_up from "../../../../src/assets/images/db-up.png";
import db_down from "../../../../src/assets/images/db_down.png";

interface EnergySavingInfoComponentType {
  isLoadingEnergySavingsData: boolean;
  energySaving: any;
  isLoadingEnergyGraphData: boolean;
  energySavingGraphData: any;
}

const EnergySavingInfoComponent: FC<EnergySavingInfoComponentType> = ({
  isLoadingEnergySavingsData,
  energySaving,
  isLoadingEnergyGraphData,
  energySavingGraphData,
}) => {
  return (
    <B2Col xs={12} lg={6} className="mb-3 mb-lg-0 px-lg-2">
      <B2Card className="p-3">
        <B2Row className="d-flex align-items-center">
          {/*  Energy saving status Section  */}
          <h4 className="mb-3 fw-bold d-block d-md-none text-center">
            Energy Saving
          </h4>
          <B2Col
            xs={12}
            md={6}
            className="d-flex flex-column justify-content-center text-center text-md-start mb-5 mb-sm-0 pe-lg-0"
          >
            <div className="d-flex flex-column gap-2">
              <div className="textNeutral600">
                <h4 className="m-0 fw-bold d-none d-md-block">Energy Saving</h4>
              </div>

              <div className="d-flex  align-items-start">
                <div className="w-50">
                  {isLoadingEnergySavingsData ? (
                    <Spinner
                      animation="border"
                      variant="secondary"
                      className="mt-1"
                    />
                  ) : (
                    <h2
                      className="textNeutral600 fw-bold"
                      title={`Life time savings: ${
                        energySaving?.lifetimesavingsinkwh?.toFixed(2) || 0
                      } kWh`}
                    >
                      {
                        formatEnergySavings(
                          energySaving?.lifetimesavingsinkwh || 0,
                          "kWh"
                        ).value
                      }{" "}
                      <span className="fs-6 textNeutral600">
                        {
                          formatEnergySavings(
                            energySaving?.lifetimesavingsinkwh || 0,
                            "kWh"
                          ).unit
                        }
                      </span>
                    </h2>
                  )}

                  <div className="">
                    <p className="m-0 textNeutral600">Cumulative</p>

                    <h5
                      style={{ marginTop: "13px" }}
                      className="mb-0 textNeutral600 fw-medium"
                    >
                      ≈{" "}
                      {isLoadingEnergySavingsData ? (
                        <Spinner
                          animation="border"
                          variant="secondary"
                          size="sm"
                          className="mt-1"
                        />
                      ) : (
                        (energySaving?.savingscoinkg / 1000).toFixed(1)
                      )}{" "}
                      tCO
                      <sub>2</sub>
                    </h5>
                  </div>
                </div>

                <div className="d-flex flex-column gap-2">
                  {isLoadingEnergySavingsData ? (
                    <Spinner
                      animation="border"
                      variant="secondary"
                      className="mt-1"
                    />
                  ) : (
                    <h2
                      className="textNeutral600 fw-bold mb-0 "
                      title={`Last month savings: ${
                        energySaving?.savedforthismonthinkwh?.toFixed(2) || 0
                      } kWh`}
                    >
                      {
                        formatEnergySavings(
                          energySaving?.savedforthismonthinkwh || 0,
                          "kWh"
                        ).value
                      }{" "}
                      <span className="fs-6 fw-bold">
                        {
                          formatEnergySavings(
                            energySaving?.savedforthismonthinkwh || 0,
                            "kWh"
                          ).unit
                        }
                      </span>
                    </h2>
                  )}

                  <div className="">
                    <p className="m-0 textNeutral600">{getFullMonthName(energySavingGraphData?.xAxisData?.[energySavingGraphData?.xAxisData?.length - 1])}</p>

                    <h5
                      style={{ marginTop: "13px" }}
                      className="mb-0 fw-medium textNeutral600 text-nowrap"
                    >
                      {isLoadingEnergySavingsData ? (
                        <Spinner
                          animation="border"
                          variant="secondary"
                          size="sm"
                          className="me-2"
                        />
                      ) : (
                        <span className="text-success textNeutral600 fw-medium">
                          {roundNumber(
                            energySaving?.savingsagainstnonsmartpercentage
                          ) || 0}
                          % &nbsp;
                        </span>
                      )}
                      vs Non Smart
                    </h5>
                  </div>
                </div>
              </div>
            </div>
          </B2Col>

          {/* Energy saving chart Section */}
          <B2Col
            xs={12}
            md={6}
            className="d-flex flex-column justify-content-flex-end px-lg-0"
          >
            {isLoadingEnergyGraphData ? (
              <div className="text-center">
                <Spinner animation="grow" variant="primary" />
              </div>
            ) : (
              <div>
                <div className="text-end pe-5">
                  <img
                    src={
                      energySaving?.percentagechangeagainstprevmonth >= 0
                        ? db_up
                        : db_down
                    }
                    alt="db_up"
                  />
                  <span
                    className="textNeutral600"
                    style={{ marginTop: "0.5px" }}
                  >
                    <span
                      className={
                        energySaving?.percentagechangeagainstprevmonth >= 0
                          ? "text-success ms-2"
                          : "text-danger ms-2"
                      }
                    >
                      {Math.abs(
                        Number(
                          energySaving?.percentagechangeagainstprevmonth?.toFixed(
                            0
                          )
                        )
                      ) || 0}
                      %
                    </span>{" "}
                    vs {getFullMonthName(energySavingGraphData?.xAxisData?.[energySavingGraphData?.xAxisData?.length - 2])}
                  </span>
                </div>
                <DashboardTrandingChart
                  seriesData={energySavingGraphData?.seriesData}
                  xAxisCategory={energySavingGraphData?.xAxisData}
                  extraData={energySavingGraphData?.extraData}
                  xToolTip="Month"
                  yToolTip="Energy Saving"
                  hr={"kWh"}
                />
              </div>
            )}
          </B2Col>
        </B2Row>
      </B2Card>
    </B2Col>
  );
};

export default EnergySavingInfoComponent;
